import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { FloatingArrow, FloatingPortal, arrow, autoUpdate, flip, offset, shift, useDismiss, useFloating, useFocus, useHover, useInteractions, useRole, useTransitionStyles, } from "@floating-ui/react";
import clsx from "clsx";
import { cloneElement, forwardRef, isValidElement, memo, useEffect, useRef, useState, } from "react";
import { mergeRefs } from "react-merge-refs";
import { Grid } from "./Grid";
import { ColorsProvider } from "../providers/ColorsProvider";
import { SlotsProvider } from "../providers/SlotsProvider";
import { useDefaultProps } from "../providers/utils/defaultProps";
import { mergeSx } from "../system";
import { Box } from "./Box";
import { Typography } from "./Typography";
export const tooltipClasses = {
    root: "MinervaTooltip-root",
    "content-wrapper": "MinervaTooltip-contentWrapper",
    "start-slot": "MinervaTooltip-startSlot",
    "end-slot": "MinervaTooltip-endSlot",
    "body-slot": "MinervaTooltip-bodySlot",
    "start-node": "MinervaTooltip-startNode",
    "end-node": "MinervaTooltip-endNode",
    "body-node": "MinervaTooltip-bodyNode",
    title: "MinervaTooltip-title",
    subtitle: "MinervaTooltip-subtitle",
};
export const tooltipSlots = {
    title: Typography,
    subtitle: Typography,
};
export const Tooltip = memo(forwardRef((props, ref) => {
    const { title, subtitle, openDelay = 100, closeDelay = 200, placement = "bottom", disabled = false, disableHover = false, disableFocus = false, open: initOpen, onOpenChange, startNode, endNode, bodyNode, density = "comfortable", color = "surface", colorSet, invertColors, slots, slotProps, className, children, sx, ...rest } = useDefaultProps({ component: "Tooltip", props });
    const [isOpen, setIsOpen] = useState(initOpen);
    const arrowRef = useRef(null);
    const { refs, floatingStyles, context } = useFloating({
        placement,
        open: isOpen,
        onOpenChange: (open) => {
            setIsOpen(open);
            onOpenChange?.(open);
        },
        middleware: [
            offset(10),
            flip(),
            shift(),
            arrow({
                element: arrowRef,
            }),
        ],
        whileElementsMounted: autoUpdate,
    });
    const hover = useHover(context, {
        move: false,
        enabled: !disableHover && !disabled,
        delay: { open: openDelay, close: closeDelay },
    });
    const focus = useFocus(context, {
        enabled: !disableFocus && !disabled,
    });
    const { isMounted, styles: transitionStyles } = useTransitionStyles(context);
    const dismiss = useDismiss(context);
    const role = useRole(context, {
        role: "tooltip",
    });
    const { getReferenceProps, getFloatingProps } = useInteractions([
        hover,
        focus,
        dismiss,
        role,
    ]);
    useEffect(() => {
        setIsOpen(initOpen);
    }, [initOpen]);
    return (_jsx(SlotsProvider, { baseSlots: tooltipSlots, slots: slots, children: ({ title: Title, subtitle: Subtitle }) => (_jsx(ColorsProvider, { color: color, colorSet: colorSet, invertColors: invertColors, children: (colorStyles) => (_jsxs(_Fragment, { children: [isValidElement(children) &&
                        cloneElement(children, {
                            ref: mergeRefs([refs.setReference, ref]),
                            ...getReferenceProps(),
                        }), isMounted && (_jsx(FloatingPortal, { children: _jsxs(Box, { ref: refs.setFloating, className: clsx([tooltipClasses.root, className]), sx: mergeSx({
                                ...colorStyles,
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                padding: density === "comfortable"
                                    ? 3
                                    : density === "dense"
                                        ? 1
                                        : 0,
                                maxWidth: 100,
                                borderRadius: (theme) => theme.radius.default,
                                boxShadow: (theme) => theme.shadows.md,
                                [`&.${tooltipClasses.root}`]: {
                                    zIndex: (theme) => theme.zIndex.tooltip,
                                    ...floatingStyles,
                                    ...transitionStyles,
                                },
                            }, sx), ...getFloatingProps(), ...rest, children: [_jsx(FloatingArrow, { ref: arrowRef, context: context, fill: colorStyles.background }), _jsxs(Grid, { container: true, gap: 1, alignItems: "center", className: tooltipClasses["content-wrapper"], children: [startNode && (_jsx(Grid, { item: true, className: clsx([
                                                tooltipClasses["start-slot"],
                                                tooltipClasses["start-node"],
                                            ]), children: startNode })), (title || subtitle) && (_jsxs(Grid, { item: true, xs: true, minWidth: 0, display: "flex", flexDirection: "column", children: [typeof title === "string" ? (_jsx(Title, { variant: "body3", fontWeight: "semibold", ...slotProps?.title, className: clsx([
                                                        tooltipClasses.title,
                                                        slotProps?.title?.className,
                                                    ]), children: title })) : (title), typeof subtitle === "string" ? (_jsx(Subtitle, { variant: "body3", ...slotProps?.subtitle, className: clsx([
                                                        tooltipClasses.subtitle,
                                                        slotProps?.subtitle?.className,
                                                    ]), children: subtitle })) : (subtitle)] })), endNode && (_jsx(Grid, { item: true, className: clsx([
                                                tooltipClasses["end-slot"],
                                                tooltipClasses["end-node"],
                                            ]), children: endNode }))] }), bodyNode && (_jsx(Grid, { item: true, width: "100%", className: clsx([
                                        tooltipClasses["body-slot"],
                                        tooltipClasses["body-node"],
                                    ]), children: bodyNode }))] }) }))] })) })) }));
}));
//# sourceMappingURL=Tooltip.js.map